'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import { SilkSheet } from '@/components/ui/silk-sheet';
import { cn } from '@/lib/ui/utils';
import { RankEmblem } from './rank-emblem';
interface Rank {
  id: string;
  order: number;
  key: string;
  name: string;
  minPoints: string;
}
interface LevelLadderModalProps {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  ladder: Rank[];
  currentLifetime: bigint | string;
}

/**
 * The public 30-rank progression ladder, presented in a detached Silk modal
 * (opened from the rewards dashboard) instead of sitting on the main page.
 */
export function LevelLadderModal(t0) {
  const $ = _c(13);
  const {
    open,
    onOpenChange,
    ladder,
    currentLifetime
  } = t0;
  const t = useTranslations("rewards");
  let t1;
  if ($[0] !== t) {
    t1 = t("ladder.title");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t) {
    t2 = t("ladder.description");
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== currentLifetime || $[5] !== ladder) {
    t3 = <LadderGrid ladder={ladder} currentLifetime={currentLifetime} />;
    $[4] = currentLifetime;
    $[5] = ladder;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== onOpenChange || $[8] !== open || $[9] !== t1 || $[10] !== t2 || $[11] !== t3) {
    t4 = <SilkSheet open={open} onOpenChange={onOpenChange} variant="detached" size="xl" title={t1} description={t2}>{t3}</SilkSheet>;
    $[7] = onOpenChange;
    $[8] = open;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
}
function LadderGrid(t0) {
  const $ = _c(9);
  const {
    ladder,
    currentLifetime
  } = t0;
  const lifetime = BigInt(currentLifetime);
  let t1;
  if ($[0] !== lifetime) {
    t1 = (acc, r, i) => lifetime >= BigInt(r.minPoints) ? i : acc;
    $[0] = lifetime;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const currentIndex = ladder.reduce(t1, -1);
  let t2;
  if ($[2] !== currentIndex || $[3] !== ladder) {
    let t3;
    if ($[5] !== currentIndex) {
      t3 = (r_0, i_0) => {
        const isCurrent = i_0 === currentIndex;
        const isLocked = i_0 > currentIndex;
        return <div key={r_0.id} data-testid={`ladder-tile-${r_0.key}`} className={cn("flex flex-col items-center gap-1 rounded-panel-inset p-2 text-center", isCurrent && "border border-border-brand bg-background-brand", isLocked && "opacity-50")}><RankEmblem rankKey={r_0.key} size={40} /><span className="text-caption text-foreground-primary">{r_0.name}</span><span className="text-caption text-foreground-tertiary">{r_0.minPoints}</span></div>;
      };
      $[5] = currentIndex;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    t2 = ladder.map(t3);
    $[2] = currentIndex;
    $[3] = ladder;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[7] !== t2) {
    t3 = <div data-testid="level-ladder" className="grid grid-cols-3 gap-2 sm:grid-cols-5">{t2}</div>;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
}